import { useEffect, useMemo } from "react";
import { trim } from "lodash";

import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import TextComboInput from "@shared/v2/TextComboInput";
import Toggle from "@shared/v2/Toggle";
import { headline2 } from "@shared/styles";
import FormAgentOrPondSelect from "@shared/FormAgentOrPondSelect";
import UploadPersonAvatarField from "../UploadPersonAvatarField";
import { containerView } from "./styles";
import { setPersonValue, uploadAvatar } from "../../reducers/contactsReducer";
import {
  useContactFormOptions,
  useContactPerson,
  useAvatarLoading,
} from "../../reducers/contactsReducer/selectors";
import { SpinnerSolidV6 } from "@shared/v2/Icomoon";

const INTENT_OPTIONS = [
  { label: "N/A", value: "n/a" },
  { label: "Buyer", value: "buyer" },
  { label: "Seller", value: "seller" },
];

function GeneralView({ modalType }) {
  const dispatch = useDispatch();
  const formOptions = useContactFormOptions();
  const avatarLoading = useAvatarLoading();
  const person = useContactPerson();
  const stageOptions = useMemo(
    () => [
      { label: "Lead Stages", options: formOptions?.leadStageOptions || [] },
      { label: "Candidate/Recruit Stages", options: formOptions?.candidateStageOptions || [] },
    ],
    [formOptions],
  );

  useEffect(() => {
    if (person.id) return;
    if (person.typeName === "lead") {
      dispatch(setPersonValue({ key: "stage", value: "New lead" }));
      dispatch(setPersonValue({ key: "stageType", value: "lead" }));
    } else {
      dispatch(setPersonValue({ key: "stage", value: "" }));
      dispatch(setPersonValue({ key: "stageType", value: "" }));
    }
  }, [person.typeName]);

  return (
    <div className={containerView}>
      <div className={`tw-text-neutral-gray-75 ${headline2}`}>General</div>
      <div className="tw-flex tw-flex-wrap tw-gap-[24px]">
        <TextComboInput
          label="First Name"
          isRequired
          value={person.firstName || ""}
          containerClassName="tw-flex-1 tw-min-w-[35%]"
          onChange={(e) => dispatch(setPersonValue({ key: "firstName", value: e.target.value }))}
          data-cy="first_name"
          error={trim(person.firstName) === ""}
        />

        <TextComboInput
          label="Last Name"
          isRequired
          value={person.lastName || ""}
          containerClassName="tw-flex-1 tw-min-w-[35%]"
          onChange={(e) => dispatch(setPersonValue({ key: "lastName", value: e.target.value }))}
          data-cy="last_name"
          error={trim(person.lastName) === ""}
        />

        {modalType === "lead" && (
          <Dropdown
            containerClassName="tw-flex-1 tw-min-w-[35%]"
            label="Person Type"
            options={formOptions?.typeNameOptions}
            value={formOptions?.typeNameOptions?.find((option) => option.value === person.typeName)}
            onChange={(option) => dispatch(setPersonValue({ key: "typeName", value: option.value }))}
            menuShouldComeToFront
          />
        )}

        {!["websiteProfile", "lender"].includes(modalType) && (
          <Dropdown
            containerClassName="tw-flex-1 tw-min-w-[35%]"
            label="Source"
            placeholder="Select Source"
            options={formOptions?.sourceOptions}
            value={formOptions?.sourceOptions?.find((option) => option.value === person.sourceId)}
            onChange={(option) => dispatch(setPersonValue({ key: "sourceId", value: option.value }))}
            isRequired={modalType === "lead"}
            isSearchable
            menuShouldComeToFront
          />
        )}

        {modalType === "lead" && person.typeName === "lead" && (
          <Dropdown
            containerClassName="tw-flex-1 tw-min-w-[35%]"
            label="Stage"
            isRequired
            options={stageOptions}
            value={[...(stageOptions[0]?.options || []), ...(stageOptions[1]?.options || [])].find(
              (option) => option.value === person.stage,
            )}
            onChange={(option) => {
              dispatch(setPersonValue({ key: "stage", value: option.value }));
              dispatch(setPersonValue({ key: "stageType", value: option.type }));
            }}
            data-cy="stage_field"
            menuShouldComeToFront
          />
        )}

        {modalType === "lead" && (
          <Dropdown
            containerClassName="tw-flex-1 tw-min-w-[35%]"
            label="Status"
            options={formOptions?.statusOptions}
            onChange={(option) => dispatch(setPersonValue({ key: "status", value: option.value }))}
            value={formOptions?.statusOptions?.find((option) => option.value === person.status)}
            menuShouldComeToFront
          />
        )}

        {!["websiteProfile", "lender"].includes(modalType) && (
          <Dropdown
            containerClassName="tw-flex-1 tw-min-w-[35%]"
            label="Intent"
            options={modalType === "lead" ? formOptions?.leadTypeOptions : INTENT_OPTIONS}
            onChange={(option) => dispatch(setPersonValue({ key: "leadType", value: option.value }))}
            value={
              (modalType === "lead" ? formOptions?.leadTypeOptions : INTENT_OPTIONS)?.find(
                (option) => option.value === person.leadType,
              ) || null
            }
            menuShouldComeToFront
          />
        )}
        {person.typeName === "lead" && <div className="tw-flex-1" />}
      </div>

      {modalType === "lead" && !person.id && (
        <div>
          <FieldLabel label="Primary Agent" />
          <FormAgentOrPondSelect
            placeholder="Select an Agent or Pond"
            enableLeadPonds
            leadPond={person.leadPondId}
            primaryAgent={person.primaryAgentId}
            onChange={(params) => {
              dispatch(setPersonValue({ key: "leadPondId", value: params.lead_pond }));
              dispatch(setPersonValue({ key: "primaryAgentId", value: params.primary_agent }));
            }}
          />
        </div>
      )}

      {person.id && (
        <div className="tw-flex tw-gap-[24px]">
          <div className="tw-relative tw-flex-1 tw-flex tw-flex-col tw-gap-[8px]">
            <FieldLabel
              label="Profile Photo"
              toolTipInfoContent={
                <div className="tw-w-[267px] tw-text-left">
                  For an ideal experience on your Brivity website, please upload an image that is cropped
                  square and is under 1MB in size. 5MB limit.
                </div>
              }
              toolTipPlacement="top"
            />
            <UploadPersonAvatarField
              person={person}
              onUpdate={(file) => dispatch(uploadAvatar({ file, personId: person.id }))}
              onRemove={() => {
                dispatch(setPersonValue({ key: "avatarUrl", value: null }));
                dispatch(setPersonValue({ key: "avatar", value: null }));
              }}
            />
            {avatarLoading && (
              <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-10">
                <SpinnerSolidV6 className="tw-animate-spin" size="xxl" />
              </div>
            )}
          </div>
          <div className="tw-flex-1" />
        </div>
      )}
      {!["websiteProfile", "lender"].includes(modalType) && (
        <Toggle
          id="ecgv_toggle"
          checked={typeof person.isLead === "undefined" ? modalType === "lead" : person.isLead}
          onChange={() => dispatch(setPersonValue({ key: "isLead", value: !person.isLead }))}
          text="Show in Leads Index"
        />
      )}
    </div>
  );
}

GeneralView.propTypes = {
  modalType: PropTypes.string.isRequired,
};

export default GeneralView;
